import type { HealthLiveness, HealthStatusResponse } from './types';

const DEFAULT_BASE = 'http://127.0.0.1:9010';
const FETCH_TIMEOUT_MS = 8000;

export class HealthMonitorError extends Error {
  constructor(message: string, public readonly cause?: unknown) {
    super(message);
    this.name = 'HealthMonitorError';
  }
}

function resolveBaseUrl(): string {
  const raw = process.env.REACT_APP_API_URL_health_monitor || DEFAULT_BASE;
  return raw.replace(/\/+$/, '');
}

async function fetchJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const controller = new AbortController();
  const onAbort = () => controller.abort();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', onAbort, { once: true });
  }

  const timer = window.setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  try {
    const res = await fetch(`${resolveBaseUrl()}${path}`, {
      method: 'GET',
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) {
      throw new HealthMonitorError(`Health monitor returned HTTP ${res.status} for ${path}`);
    }
    return (await res.json()) as T;
  } catch (err) {
    if (err instanceof HealthMonitorError) throw err;
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new HealthMonitorError('Health monitor request timed out or was cancelled', err);
    }
    const message = err instanceof Error ? err.message : 'Unknown error';
    throw new HealthMonitorError(`Failed to reach health monitor (${path}): ${message}`, err);
  } finally {
    window.clearTimeout(timer);
    if (signal) signal.removeEventListener('abort', onAbort);
  }
}

export function fetchHealth(signal?: AbortSignal): Promise<HealthLiveness> {
  return fetchJson<HealthLiveness>('/health', signal);
}

export function fetchStatus(signal?: AbortSignal): Promise<HealthStatusResponse> {
  return fetchJson<HealthStatusResponse>('/api/v1/status', signal);
}

/**
 * Primary source is GET /api/v1/status.
 * GET /health is best-effort for the monitor liveness chip only.
 */
export async function fetchHealthBundle(signal?: AbortSignal): Promise<{
  health: HealthLiveness;
  status: HealthStatusResponse;
}> {
  const status = await fetchStatus(signal);
  let health: HealthLiveness = { status: status.status === 'ok' ? 'ok' : status.status };
  try {
    health = await fetchHealth(signal);
  } catch {
    // /api/v1/status is authoritative; ignore /health failures
  }
  return { health, status };
}

